import React, { lazy, Suspense, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { getIsPWAInstalled } from 'helpers/getIsPWAInstalled';
import PropTypes from 'prop-types';
import { isCasinoGamePage } from 'utils';

import {
  printMissingConfigError,
  SiteConfigManager,
  useSettings,
  useUserMessages,
} from '@atlas/core';
import { COMPONENT_TYPES } from '@atlas/routing';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { PLATFORM_TYPES } from 'common/constants';
import LabelTranslateWithLink from 'common/I18n/Label';
import { defaultRoutes } from 'components/MainRouter/constants';
import { useMessagesTabConfig } from 'src/hooks/useMessagesTabConfig';

import NavigationBarItem from './NavigationBarItem';

const BetslipPopupButton = lazy(
  () => import(/* webpackChunkName: "Betslip" */ 'common/Betslip/BetslipPopupButton'),
);

const { MOBILE, MULTIPLATFORM } = PLATFORM_TYPES;

const { CLIENT_PROFILE, MENU } = COMPONENT_TYPES;

const { account: accountDefaultRoute, menu: menuDefaultRoute } = defaultRoutes;

const NavigationBarItemList = ({ links }) =>
  links.map(({ altIcon, defaultPath, icon, iconType, lightIcon, name, path, withNotification }) => (
    <LabelTranslateWithLink
      key={path || defaultPath}
      message={name}
      render={t => (
        <NavigationBarItem
          altIcon={altIcon}
          icon={icon}
          iconType={iconType}
          label={t}
          lightIcon={lightIcon}
          link={path || defaultPath}
          withNotification={withNotification}
        />
      )}
    />
  ));

NavigationBarItemList.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      defaultPath: PropTypes.string,
      icon: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
};

const NavigationBar = () => {
  // state of the betslip popup
  const { betIdToEdit } = useLocalBetslip();
  const { quickBet: isQuickBet } = useSettings();
  const { unreadMessagesCount } = useUserMessages();
  const navigation = SiteConfigManager.getConfig('navigation.top', []);
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);
  const {
    location: { pathname },
  } = useHistory();
  const messagesTab = useMessagesTabConfig();

  const links = useMemo(
    () =>
      navigation
        .filter(el => el.platform_type === MULTIPLATFORM || el.platform_type === MOBILE)
        .map(
          ({
            alt_icon: altIcon,
            icon,
            icon_type: iconType,
            light_icon: lightIcon,
            location,
            name,
          }) => ({
            altIcon,
            icon,
            iconType,
            lightIcon,
            name,
            path: location,
          }),
        )
        .sort((a, b) => (a.order_index > b.order_index ? -1 : 1)),
    [],
  );

  const leftLinks = useMemo(() => {
    const linksToRender = [
      links[0] || {
        icon: 'icon-account',
        iconType: 'predefined',
        name: 'client profile',
        path: SiteConfigManager.getComponentPath(CLIENT_PROFILE) || accountDefaultRoute,
      },
      links[1] || {
        icon: 'icon-account',
        iconType: 'predefined',
        name: 'client profile',
        path: SiteConfigManager.getComponentPath(CLIENT_PROFILE) || accountDefaultRoute,
      },
    ];

    return <NavigationBarItemList links={linksToRender} />;
  }, [links]);

  const rightLinks = useMemo(() => {
    const linksToRender = [
      links[2] || {
        icon: 'icon-account',
        iconType: 'predefined',
        name: 'client profile',
        path: SiteConfigManager.getComponentPath(CLIENT_PROFILE) || accountDefaultRoute,
      },
      // menu are always the forth item of the navigation bar
      {
        icon: 'i-menu',
        iconType: 'predefined',
        name: 'Menu',
        path: SiteConfigManager.getComponentPath(MENU) || menuDefaultRoute,
        withNotification: !!unreadMessagesCount && messagesTab?.isActive,
      },
    ];

    return <NavigationBarItemList links={linksToRender} />;
  }, [links, messagesTab?.isActive, unreadMessagesCount]);

  const navigationClass = cx('navigation', {
    hidden: isCasinoGamePage(pathname, [casinoPath, casinoLivePath]),
    shadow: isQuickBet || betIdToEdit,
  });
  const navBarClass = cx('navigation-tab-bar', 'betslip', { 'is-pwa': getIsPWAInstalled() });

  if (navigation.length === 0) {
    printMissingConfigError('navigation.top');
  }

  return (
    <div className={navigationClass}>
      <div className={navBarClass}>
        {leftLinks}
        <Suspense fallback={<div />}>
          <BetslipPopupButton />
        </Suspense>
        {rightLinks}
      </div>
    </div>
  );
};

export default NavigationBar;
